import { fmtDate, fmtDateTime, humanizeKey } from "./format";
import { LD_RISK_NOTE_KEYS, RISK_NOTE_KEYS, buildDispatchBriefText } from "./selectors";

/**
 * Notes Hub: every note the MC wrote during the client's journey, grouped by
 * the stage (tab) it was written on, in journey order. Nothing new is stored —
 * each stage already persists its own fields; this just reads them back.
 */

/** Journey stages, in order. `label` differs for local and long distance where the plan names them differently. */
const STAGES = [
  { key: "intro", label: "Introduction call" },
  { key: "confirmation", label: "Confirmation call" },
  { key: "inProgress", label: "Move in progress", ldLabel: "In progress" },
  { key: "inTransit", label: "In transit" },
  { key: "deliveryDay", label: "Delivery day" },
  { key: "postMove", label: "Post move" },
  { key: "general", label: "General" },
  { key: "other", label: "Other" },
];

/** s2Notes key → [stage, label]. A key not listed here lands under "Other". */
const S2_FIELDS = {
  // Introduction call
  introFeedback: ["intro", "Feedback"],
  originNote: ["intro", "Origin"],
  destNote: ["intro", "Destination"],
  pickupNotes: ["intro", "Pickup notes"],
  deliveryNotes: ["intro", "Delivery notes"],
  pickupBuildingReq: ["intro", "Pickup building requirements"],
  deliveryBuildingReq: ["intro", "Delivery building requirements"],
  pickupMasonite: ["intro", "Masonite at pickup"],
  deliveryMasonite: ["intro", "Masonite at delivery"],
  longCarryPickup: ["intro", "Long carry at pickup"],
  longCarryDelivery: ["intro", "Long carry at delivery"],
  elevatorRestrictionNote: ["intro", "Elevator at pickup"],
  elevatorRestrictionNoteDelivery: ["intro", "Elevator at delivery"],
  stairsFlights: ["intro", "Stairs at pickup"],
  stairsFlightsDelivery: ["intro", "Stairs at delivery"],
  stairsFlightsNote: ["intro", "Stairs at pickup"],
  stairsFlightsNoteDelivery: ["intro", "Stairs at delivery"],
  specialServicesNotes: ["intro", "Special services"],
  paymentNotes: ["intro", "Payment notes"],
  paymentSchedule: ["intro", "Payment schedule"],
  scopeChanges: ["intro", "Scope changes"],
  salesRepNeeded: ["intro", "Sales rep needed"],
  delicateItems: ["intro", "Delicate items"],
  crates: ["intro", "Crates"],
  unpacking: ["intro", "Unpacking"],
  contactless: ["intro", "Contactless"],
  coisArranged: ["intro", "COIs arranged"],
  coverageExplained: ["intro", "Coverage explained"],
  replacementCoverage: ["intro", "Replacement coverage"],
  fragileItems: ["intro", "Fragile or high-value items"],
  coverageExplainedNote: ["intro", "Coverage explained — note"],
  // Confirmation call
  logisticsNotes: ["confirmation", "Logistics notes"],
  storageNotes: ["confirmation", "Storage notes"],
  storageStartDate: ["confirmation", "Storage start date"],
  storageMonthlyRate: ["confirmation", "Storage monthly rate"],
  ltsOutDate: ["confirmation", "LTS out of storage date"],
  storageHandling: ["confirmation", "Storage handling"],
  firstStorageCharge: ["confirmation", "First storage charge"],
  storageDuration: ["confirmation", "Storage duration"],
  confPaymentNotes: ["confirmation", "Payment notes"],
  confCrewNotes: ["confirmation", "Notes for the crew"],
  confCoiPickup: ["confirmation", "COI at pickup"],
  confCoiDelivery: ["confirmation", "COI at delivery"],
  confContractSigned: ["confirmation", "Contract signed"],
  confLiabilityExplained: ["confirmation", "Liability explained"],
  confFullCoverageIncluded: ["confirmation", "Full coverage included"],
  shuttleRate: ["confirmation", "Shuttle rate"],
  openBalanceBeforePickup: ["confirmation", "Open balance before pickup"],
  pickupWindow: ["confirmation", "Pickup window"],
  revisionReviewNote: ["confirmation", "Reviewed change with client"],
  // Move in progress
  dispatchedCrew: ["inProgress", "Dispatched crew"],
  // In transit (LD)
  paymentPreferenceNote: ["inTransit", "Payment preference"],
  // Delivery day (LD)
  deliveryElevatorRestriction: ["deliveryDay", "Elevator restriction"],
  // Post move
  postMoveNotes: ["postMove", "Post move notes"],
  automatedFlowNotes: ["postMove", "Automated flow notes"],
  crewFeedbackSalesperson: ["postMove", "Crew feedback for the salesperson"],
  feedbackOperations: ["postMove", "Feedback for operations"],
  priceAdjustments: ["postMove", "Price adjustments"],
};

/** Risk notes live in s2Notes too, keyed per risk; label them with the risk. */
const riskFields = () => {
  const out = {};
  [RISK_NOTE_KEYS, LD_RISK_NOTE_KEYS].forEach((map) =>
    Object.keys(map).forEach((risk) => {
      out[map[risk]] = ["confirmation", "Risk — " + risk];
    }),
  );
  return out;
};

/** Plain job fields (not in s2Notes) that hold a stage's note. */
const JOB_FIELDS = [
  ["inProgressFeedback", "inProgress", "Feedback"],
  ["transitNote", "inTransit", "Note"],
  ["postMoveNote", "postMove", "Note"],
  ["riskNote", "confirmation", "Risk note"],
];

const clean = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * @returns {{key:string,label:string,notes:{key?:string,label:string,text:string,author?:string,date?:string}[]}[]}
 *   stages that have at least one note, in journey order.
 */
export const notesByStage = (job, isLD) => {
  const byStage = {};
  const add = (stage, note) => {
    if (!byStage[stage]) byStage[stage] = [];
    byStage[stage].push(note);
  };

  JOB_FIELDS.forEach(([field, stage, label]) => {
    const text = clean(job[field]);
    if (text) add(stage, { key: field, label, text });
  });
  if (clean(job._s2feedback))
    add("general", { key: "_s2feedback", label: "Customer Success", text: clean(job._s2feedback) });

  const fields = { ...S2_FIELDS, ...riskFields() };
  const s2 = job.s2Notes && !Array.isArray(job.s2Notes) ? job.s2Notes : {};
  Object.keys(s2).forEach((k) => {
    const text = clean(s2[k]);
    if (!text) return;
    const [stage, label] = fields[k] || ["other", humanizeKey(k)];
    add(stage, { key: k, label, text });
  });

  (job.notes || []).forEach((n) => {
    const text = clean(n.text);
    if (text) add("general", { label: "Note", text, author: n.author, date: n.date });
  });

  // A transit or delivery-day note on a local job is still shown — it was written, so it counts.
  return STAGES.filter((s) => byStage[s.key]).map((s) => ({
    key: s.key,
    label: isLD && s.ldLabel ? s.ldLabel : s.label,
    notes: byStage[s.key],
  }));
};

/** One upper-case heading per stage, one line per note. */
const formatGroups = (groups, headingSuffix = "") =>
  groups
    .map((g) => {
      const lines = g.notes.map((n) => {
        if (g.key === "general") {
          const who = [n.author, n.date ? fmtDateTime(n.date) : ""].filter(Boolean).join(", ");
          return "- " + n.text + (who ? ` (${who})` : "");
        }
        return `- ${n.label}: ${n.text}`;
      });
      return [g.label.toUpperCase() + headingSuffix].concat(lines).join("\n");
    })
    .join("\n\n");

/** The copy-pastable version: one upper-case heading per stage, one line per note. */
export const buildNotesSummaryText = (job, isLD) => {
  const head = `NOTES — ${job.clientName} · ${job.zohoId} · move ${fmtDate(job.moveDate)}`;
  const groups = notesByStage(job, isLD);
  if (!groups.length) return head + "\n\nNo notes yet.";
  return [head, ""].concat(formatGroups(groups)).join("\n");
};

/** Yes/No answers the MC gave (s2YN), in the words of the question. */
const YN_FIELDS = [
  ["coverageExplained", "Coverage explained to client"],
  ["paymentSchedule", "Payment schedule explained"],
  ["confCoiPickup", "COI at pickup done"],
  ["confCoiDelivery", "COI at delivery done"],
  ["sitIncluded", "LTS included"],
  ["storageDelivery", "Storage at destination"],
];

const paymentMethodLabel = (value) =>
  ({ credit_card: "Credit card", wire: "Wire transfer", check: "Check", debit_card: "Debit card" })[value] ||
  humanizeKey(value || "");

/** Notes the dispatch brief above already prints, so they are not repeated below it. */
const briefKeys = (job) => {
  const keys = new Set(["specialServicesNotes", "confCrewNotes"]);
  (job.riskFlags || []).forEach((r) => {
    if (RISK_NOTE_KEYS[r]) keys.add(RISK_NOTE_KEYS[r]);
  });
  return keys;
};

/**
 * The "Copy-Paste Version — for Zoho Dispatch Notes": the dispatch brief plus
 * everything the MC typed or answered on the way — introduction call,
 * confirmation call, in progress and the rest — grouped by stage, so nothing
 * said to the client is lost when it is pasted into Zoho.
 */
export const buildCopyPasteText = (job, isLD) => {
  const skip = briefKeys(job);
  const groups = notesByStage(job, isLD)
    .map((g) => ({ ...g, notes: g.notes.filter((n) => !skip.has(n.key)) }))
    .filter((g) => g.notes.length);

  const yn = (job.s2YN && !Array.isArray(job.s2YN) ? job.s2YN : {});
  const answers = YN_FIELDS.filter(([k]) => yn[k] === "yes" || yn[k] === "no").map(
    ([k, label]) => `- ${label}: ${yn[k] === "yes" ? "Yes" : "No"}`,
  );
  if (job.paymentMethod) answers.unshift("- Payment method: " + paymentMethodLabel(job.paymentMethod));

  const parts = [buildDispatchBriefText(job)];
  if (answers.length) parts.push(["MC ANSWERS"].concat(answers).join("\n"));
  if (groups.length) parts.push("NOTES FROM THE MC, BY STAGE\n\n" + formatGroups(groups));
  return parts.join("\n\n");
};
